import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

interface ChatContextValue {
  isOpen: boolean;
  openChat: (message?: string) => void;
  closeChat: () => void;
  /** Read the pending prefill message (if any) and clear it. */
  consumeInitialMessage: () => string | null;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);

  const openChat = useCallback((message?: string) => {
    if (typeof message === 'string' && message.trim()) {
      pendingMessageRef.current = message.trim();
    }
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const consumeInitialMessage = useCallback(() => {
    const v = pendingMessageRef.current;
    pendingMessageRef.current = null;
    return v;
  }, []);

  return (
    <ChatContext.Provider value={{ isOpen, openChat, closeChat, consumeInitialMessage }}>
      {children}
    </ChatContext.Provider>
  );
};

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within a ChatProvider');
  return ctx;
}
